import React, { useEffect, useState } from "react";
import { FileText, Image as ImageIcon, Download, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

export const DOC_ACCEPT = ".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,application/pdf,image/jpeg,image/png,image/webp,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document";
export const DOC_MAX_BYTES = 10 * 1024 * 1024;

export const fmtSize = (n) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

export const uploadConsultationDocument = (cid, file, source = "chat", onProgress, caption = "") => {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("source", source);
  if (caption) fd.append("caption", caption);
  const cfg = { headers: { "Content-Type": "multipart/form-data" } };
  if (onProgress) cfg.onUploadProgress = (e) => onProgress(e.total ? Math.round((e.loaded / e.total) * 100) : 0);
  return api.post(`/consultations/${cid}/documents`, fd, cfg).then((r) => r.data);
};
export const listConsultationDocuments = (cid) => api.get(`/consultations/${cid}/documents`).then((r) => r.data);

export async function downloadConsultationDocument(cid, doc) {
  const res = await api.get(`/consultations/${cid}/documents/${doc.id}/download`, { responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url; a.download = doc.name; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

// Compact chip for a shared document: filename, size, inline image preview, download.
export function DocumentChip({ cid, doc, dark = false, testid }) {
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const isImage = (doc.content_type || "").startsWith("image/");

  useEffect(() => {
    if (!isImage) return undefined;
    let url;
    api.get(`/consultations/${cid}/documents/${doc.id}/download`, { responseType: "blob" })
      .then((r) => { url = URL.createObjectURL(r.data); setPreview(url); })
      .catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [cid, doc.id, isImage]);

  const dl = async () => {
    setBusy(true);
    try { await downloadConsultationDocument(cid, doc); } finally { setBusy(false); }
  };
  const Icon = isImage ? ImageIcon : FileText;
  return (
    <div className={`box-border w-full max-w-xs min-w-0 rounded-xl border ${dark ? "border-white/20 bg-white/10" : "border-slate-200 bg-white"} p-2`} data-testid={testid || `doc-chip-${doc.id}`}>
      {isImage && preview && (
        <img src={preview} alt={doc.name} className="mb-2 max-h-40 max-w-full rounded-lg object-contain" data-testid={`doc-preview-${doc.id}`} />
      )}
      <div className="flex items-center gap-2">
        <Icon size={16} className={dark ? "text-[#D4AF37]" : "text-[#0A2540]"} />
        <div className="min-w-0 flex-1">
          <p className="text-xs font-bold truncate" title={doc.name}>{doc.name}</p>
          <p className={`text-[10px] ${dark ? "text-white/60" : "text-slate-500"}`}>{fmtSize(doc.size || 0)}{doc.source === "booking" ? " · attached at booking" : ""}</p>
        </div>
        <button type="button" onClick={dl} disabled={busy} aria-label={`Download ${doc.name}`} data-testid={`doc-download-${doc.id}`}
          className={`p-1.5 rounded-lg ${dark ? "hover:bg-white/10" : "hover:bg-slate-100"}`}>
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
        </button>
      </div>
    </div>
  );
}
